/** Presentation only: acceptance remains exclusively in the shared draft contract. */
import type { TFunction } from "i18next";

import type { DraftIssue, EditorSchema } from "./schemaModel";

export function describeDraftIssue(
  issue: DraftIssue,
  schema: EditorSchema,
  lang: "fr" | "en",
  t: TFunction,
) {
  const p = issue.path.split(".");
  const section =
    p[0] === "sections" && /^\d+$/.test(p[1] ?? "") ? schema.sections[Number(p[1])] : undefined;
  const field =
    section && p[2] === "fields" && /^\d+$/.test(p[3] ?? "")
      ? section.fields[Number(p[3])]
      : undefined;
  const block =
    section && p[2] === "blocks" && /^\d+$/.test(p[3] ?? "")
      ? section.blocks[Number(p[3])]
      : undefined;
  const option =
    field && p[4] === "options" && /^\d+$/.test(p[5] ?? "")
      ? field.options?.[Number(p[5])]
      : undefined;
  const textLang = p.at(-1) === "fr" || p.at(-1) === "en" ? p.at(-1)! : undefined;
  const name = (label: { fr: string; en: string }) =>
    label[lang] || label[lang === "fr" ? "en" : "fr"];
  const labels: string[] = [];
  let targetId: string | undefined;
  if (section) {
    labels.push(
      `${t("form_templates.editor.section")} ${Number(p[1]) + 1} — ${name(section.label)}`,
    );
    targetId = `sec-${section._uid}-label-${lang}`;
  }
  if (field) {
    labels.push(`${t("form_templates.editor.field")} ${Number(p[3]) + 1} — ${name(field.label)}`);
    targetId = `f-${field._uid}-label-${lang}`;
    if (["label", "help", "placeholder"].includes(p[4] ?? "")) {
      labels.push(
        `${t(`form_templates.editor.field_${p[4]}`)}${textLang ? ` (${textLang.toUpperCase()})` : ""}`,
      );
      targetId = `f-${field._uid}-${p[4]}-${textLang ?? lang}`;
    } else if (["key", "type", "role"].includes(p[4] ?? "")) {
      const key = p[4] === "type" ? "field_type" : p[4] === "role" ? "role_label" : "key";
      labels.push(t(`form_templates.editor.${key}`));
      targetId = `f-${field._uid}-${p[4]}`;
    } else labels.push(t("form_templates.editor.validation"));
  } else if (section && p[2] === "label") {
    labels.push(
      `${t("form_templates.editor.section_label")}${textLang ? ` (${textLang.toUpperCase()})` : ""}`,
    );
    targetId = `sec-${section._uid}-label-${textLang ?? lang}`;
  } else if (section && p[2] === "key") targetId = `sec-${section._uid}-key`;
  if (block) {
    labels.push(
      `${t("form_templates.editor.block")} ${Number(p[3]) + 1}${textLang ? ` (${textLang.toUpperCase()})` : ""}`,
    );
    targetId = p[4] === "key" ? `bloc-${block._uid}-key` : `bloc-${block._uid}-${textLang ?? lang}`;
  }
  if (option) {
    labels.push(
      `${t("form_templates.editor.options")} ${Number(p[5]) + 1} — ${name(option.label)}${textLang ? ` (${textLang.toUpperCase()})` : ""}`,
    );
    targetId =
      p[6] === "value" ? `o-${option._uid}-val` : `o-${option._uid}-label-${textLang ?? lang}`;
  }
  let message = t("form_templates.editor.errors.invalid_settings");
  if (issue.code === "too_small" && issue.kind === "string" && issue.minimum === 1 && textLang) {
    message = t(
      field && ["help", "placeholder"].includes(p[4] ?? "")
        ? "form_templates.editor.errors.optional_bilingual"
        : "form_templates.editor.errors.label_required",
    );
  } else if (issue.code === "too_big" && issue.kind === "string") {
    message = t("form_templates.editor.errors.max_characters", { count: issue.maximum });
  } else if (issue.code === "too_big" && issue.kind === "array") {
    message = t("form_templates.editor.errors.max_items", { count: issue.maximum });
  } else if (issue.code === "invalid_string" && (p.at(-1) === "key" || p.at(-1) === "value")) {
    message = t("form_templates.editor.errors.key_format");
  } else if (issue.code === "custom" && issue.path === "sections") {
    message = t("form_templates.editor.errors.key_duplicate");
  }
  return { label: labels.join(" · ") || t("form_templates.editor.tab_edit"), message, targetId };
}
